import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { Input } from '../ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { cn } from '../../lib/utils';
import { Plus, Shield, Users, Edit2, Trash2, ChevronDown, ChevronRight, CheckCircle2, Loader2, AlertTriangle, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { useAppMode } from '../../contexts/AppModeContext';
import * as rolePermissionsApi from '../../lib/api/rolePermissionsApi';
import { useTeamMembers } from '../../hooks/useTeamMembers';

/* ── Backend ↔ UI label mapping (only wired for Lumina right now) ── */
const LUMINA_KEY_BY_LABEL = {
  'View patient records': 'view_patient_records',
  'Edit patient records': 'edit_patient_records',
  'Create SOAP notes': 'create_soap_notes',
  'Edit SOAP notes': 'edit_soap_notes',
  'Sign & lock notes': 'sign_lock_notes',
  'View encounter review': 'view_encounter_review',
  'Edit encounter review': 'edit_encounter_review',
  'Order labs/imaging': 'order_labs_imaging',
  'Prescribe medications': 'prescribe_medications',
  'View billing codes': 'view_billing_codes',
  'Send to billing': 'send_to_billing',
  'Access clinical focus': 'access_clinical_focus',
  'View pre-visit prep': 'view_pre_visit_prep',
  'Manage pre-visit prep': 'manage_pre_visit_prep',
  'Access Lens uploads': 'access_lens_uploads',
  'View handoff notes': 'view_handoff_notes',
  'Create handoff notes': 'create_handoff_notes',
  'Manage settings': 'manage_settings',
};


/* ── Role definitions per app ── */
const APP_ROLES = {
  lumina: {
    name: 'Lumina',
    roles: [
      {
        id: 'provider', name: 'Provider', desc: 'Full clinical access — documentation, orders, patient records, billing codes',
        color: 'bg-violet-100 text-violet-700',
        permissions: {
          'View patient records': true, 'Edit patient records': true, 'Create SOAP notes': true, 'Edit SOAP notes': true,
          'Sign & lock notes': true, 'View encounter review': true, 'Edit encounter review': true, 'Order labs/imaging': true,
          'Prescribe medications': true, 'View billing codes': true, 'Send to billing': true, 'Access clinical focus': true,
          'View pre-visit prep': true, 'Manage pre-visit prep': true, 'Access Lens uploads': true, 'View handoff notes': true,
          'Create handoff notes': true, 'Manage settings': true,
        },
      },
      {
        id: 'medical_assistant', name: 'Medical Assistant', desc: 'Pre-visit prep, vitals, document scanning, limited chart access',
        color: 'bg-blue-100 text-blue-700',
        permissions: {
          'View patient records': true, 'Edit patient records': false, 'Create SOAP notes': false, 'Edit SOAP notes': false,
          'Sign & lock notes': false, 'View encounter review': true, 'Edit encounter review': false, 'Order labs/imaging': false,
          'Prescribe medications': false, 'View billing codes': false, 'Send to billing': false, 'Access clinical focus': false,
          'View pre-visit prep': true, 'Manage pre-visit prep': true, 'Access Lens uploads': true, 'View handoff notes': true,
          'Create handoff notes': true, 'Manage settings': false,
        },
      },
      {
        id: 'nurse', name: 'Nurse / RN', desc: 'Vitals, triage, medication administration, treatment assistance',
        color: 'bg-emerald-100 text-emerald-700',
        permissions: {
          'View patient records': true, 'Edit patient records': true, 'Create SOAP notes': false, 'Edit SOAP notes': false,
          'Sign & lock notes': false, 'View encounter review': true, 'Edit encounter review': false, 'Order labs/imaging': false,
          'Prescribe medications': false, 'View billing codes': false, 'Send to billing': false, 'Access clinical focus': false,
          'View pre-visit prep': true, 'Manage pre-visit prep': true, 'Access Lens uploads': true, 'View handoff notes': true,
          'Create handoff notes': true, 'Manage settings': false,
        },
      },
      {
        id: 'scribe', name: 'Scribe', desc: 'Real-time charting under provider supervision, read/write progress notes',
        color: 'bg-amber-100 text-amber-700',
        permissions: {
          'View patient records': true, 'Edit patient records': false, 'Create SOAP notes': true, 'Edit SOAP notes': true,
          'Sign & lock notes': false, 'View encounter review': true, 'Edit encounter review': true, 'Order labs/imaging': false,
          'Prescribe medications': false, 'View billing codes': false, 'Send to billing': false, 'Access clinical focus': true,
          'View pre-visit prep': true, 'Manage pre-visit prep': false, 'Access Lens uploads': false, 'View handoff notes': true,
          'Create handoff notes': false, 'Manage settings': false,
        },
      },
      {
        id: 'clinical_admin', name: 'Clinical Admin', desc: 'User management, system configuration, reporting — no clinical data',
        color: 'bg-slate-200 text-slate-700',
        permissions: {
          'View patient records': false, 'Edit patient records': false, 'Create SOAP notes': false, 'Edit SOAP notes': false,
          'Sign & lock notes': false, 'View encounter review': false, 'Edit encounter review': false, 'Order labs/imaging': false,
          'Prescribe medications': false, 'View billing codes': true, 'Send to billing': false, 'Access clinical focus': false,
          'View pre-visit prep': false, 'Manage pre-visit prep': false, 'Access Lens uploads': false, 'View handoff notes': false,
          'Create handoff notes': false, 'Manage settings': true,
        },
      },
    ],
  },
  revq: {
    name: 'RevQ',
    roles: [
      {
        id: 'admin', name: 'Admin', desc: 'Full access to all settings, team management, and all views',
        color: 'bg-amber-100 text-amber-700',
        permissions: {
          'View claims dashboard': true, 'Submit claims': true, 'Edit claims': true, 'View denial reports': true,
          'Manage denials': true, 'View A/R aging': true, 'Manage A/R': true, 'View financial reports': true,
          'Export reports': true, 'View compliance': true, 'Manage compliance': true, 'View fee schedules': true,
          'Edit fee schedules': true, 'Manage users': true, 'View audit logs': true, 'Configure settings': true,
        },
      },
      {
        id: 'billing_supervisor', name: 'Billing Supervisor', desc: 'Full team visibility, KPI dashboard, claim assignment — no individual claim submission',
        color: 'bg-emerald-100 text-emerald-700',
        permissions: {
          'View claims dashboard': true, 'Submit claims': false, 'Edit claims': false, 'View denial reports': true,
          'Manage denials': true, 'View A/R aging': true, 'Manage A/R': true, 'View financial reports': true,
          'Export reports': true, 'View compliance': true, 'Manage compliance': false, 'View fee schedules': true,
          'Edit fee schedules': false, 'Manage users': true, 'View audit logs': true, 'Configure settings': false,
        },
      },
      {
        id: 'biller', name: 'Biller', desc: 'Reviews coded claims, runs AI validation, submits claims to payers',
        color: 'bg-violet-100 text-violet-700',
        permissions: {
          'View claims dashboard': true, 'Submit claims': true, 'Edit claims': true, 'View denial reports': true,
          'Manage denials': false, 'View A/R aging': true, 'Manage A/R': false, 'View financial reports': false,
          'Export reports': false, 'View compliance': false, 'Manage compliance': false, 'View fee schedules': true,
          'Edit fee schedules': false, 'Manage users': false, 'View audit logs': false, 'Configure settings': false,
        },
      },
      {
        id: 'coder', name: 'Coder', desc: 'Reviews charts and assigns ICD-10/CPT codes — cannot submit claims',
        color: 'bg-blue-100 text-blue-700',
        permissions: {
          'View claims dashboard': true, 'Submit claims': false, 'Edit claims': false, 'View denial reports': false,
          'Manage denials': false, 'View A/R aging': false, 'Manage A/R': false, 'View financial reports': false,
          'Export reports': false, 'View compliance': false, 'Manage compliance': false, 'View fee schedules': true,
          'Edit fee schedules': false, 'Manage users': false, 'View audit logs': false, 'Configure settings': false,
        },
      },
      {
        id: 'denials_specialist', name: 'Denials Specialist', desc: 'Works only denied/rejected claims, manages appeals and corrections',
        color: 'bg-red-100 text-red-700',
        permissions: {
          'View claims dashboard': true, 'Submit claims': false, 'Edit claims': true, 'View denial reports': true,
          'Manage denials': true, 'View A/R aging': true, 'Manage A/R': false, 'View financial reports': false,
          'Export reports': false, 'View compliance': false, 'Manage compliance': false, 'View fee schedules': true,
          'Edit fee schedules': false, 'Manage users': false, 'View audit logs': false, 'Configure settings': false,
        },
      },
    ],
  },
  echo: {
    name: 'Echo',
    roles: [
      {
        id: 'office_manager', name: 'Office Manager', desc: 'Full front-office access — staff management, scheduling, reporting, AI agent configuration',
        color: 'bg-violet-100 text-violet-700',
        permissions: {
          'View schedule': true, 'Edit schedule': true, 'Check-in patients': true, 'Manage patient flow': true,
          'View patient demographics': true, 'Edit patient demographics': true, 'Configure AI agents': true,
          'View call logs': true, 'Manage call routing': true, 'View reports': true, 'Export reports': true,
          'Manage staff': true, 'Configure settings': true, 'View billing summary': true,
        },
      },
      {
        id: 'front_desk', name: 'Front Desk', desc: 'Patient check-in/check-out, appointment scheduling, phone handling',
        color: 'bg-blue-100 text-blue-700',
        permissions: {
          'View schedule': true, 'Edit schedule': true, 'Check-in patients': true, 'Manage patient flow': true,
          'View patient demographics': true, 'Edit patient demographics': true, 'Configure AI agents': false,
          'View call logs': true, 'Manage call routing': false, 'View reports': false, 'Export reports': false,
          'Manage staff': false, 'Configure settings': false, 'View billing summary': false,
        },
      },
      {
        id: 'scheduler', name: 'Scheduler', desc: 'Appointment scheduling, provider calendar management, waitlist management',
        color: 'bg-emerald-100 text-emerald-700',
        permissions: {
          'View schedule': true, 'Edit schedule': true, 'Check-in patients': false, 'Manage patient flow': false,
          'View patient demographics': true, 'Edit patient demographics': false, 'Configure AI agents': false,
          'View call logs': false, 'Manage call routing': false, 'View reports': false, 'Export reports': false,
          'Manage staff': false, 'Configure settings': false, 'View billing summary': false,
        },
      },
      {
        id: 'intake_coordinator', name: 'Intake Coordinator', desc: 'New patient registration, insurance verification, referral processing',
        color: 'bg-amber-100 text-amber-700',
        permissions: {
          'View schedule': true, 'Edit schedule': false, 'Check-in patients': true, 'Manage patient flow': false,
          'View patient demographics': true, 'Edit patient demographics': true, 'Configure AI agents': false,
          'View call logs': false, 'Manage call routing': false, 'View reports': false, 'Export reports': false,
          'Manage staff': false, 'Configure settings': false, 'View billing summary': false,
        },
      },
      {
        id: 'medical_records', name: 'Medical Records', desc: 'Records management, release of information, document scanning',
        color: 'bg-slate-200 text-slate-700',
        permissions: {
          'View schedule': false, 'Edit schedule': false, 'Check-in patients': false, 'Manage patient flow': false,
          'View patient demographics': true, 'Edit patient demographics': false, 'Configure AI agents': false,
          'View call logs': false, 'Manage call routing': false, 'View reports': false, 'Export reports': false,
          'Manage staff': false, 'Configure settings': false, 'View billing summary': false,
        },
      },
    ],
  },
};

// ─── Team Members source-of-truth ─────────────────────────────────────
// The Users list below is intentionally NOT a hardcoded per-app fixture
// anymore. Demo mode pulls from the shared `DEMO_TEAM_MEMBERS` fixture
// (same one TeamsManager uses), and real mode calls `/api/team/members`.
// Result: the Roles tab and the Teams tab always show identical counts
// and data. See `/app/frontend/src/lib/fixtures/teamMembersDemo.js`.


const RolesPermissionsTab = ({ appKey }) => {
  const config = APP_ROLES[appKey];
  const { isDemo } = useAppMode();
  const isLuminaReal = appKey === 'lumina' && !isDemo;

  // ── Shared team store: same source of truth as Settings → Team. ──
  // This guarantees identical member counts and data across both tabs,
  // and role/schedulable mutations propagate instantly to the other tab
  // without any refetch.
  const {
    members: users,
    loading: usersLoading,
    error: usersError,
    refresh: refreshMembers,
    setRole: storeSetRole,
    updateMember: storeUpdateMember,
  } = useTeamMembers();

  const [expandedRole, setExpandedRole] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [showInvite, setShowInvite] = useState(false);
  const [inviteRole, setInviteRole] = useState('');
  const [permissions, setPermissions] = useState(() => {
    const p = {};
    if (config) config.roles.forEach((r) => { p[r.id] = { ...r.permissions }; });
    return p;
  });
  const [loadingPerms, setLoadingPerms] = useState(false);
  const [savingRoleId, setSavingRoleId] = useState(null);

  /* ── Hydrate permissions from backend (Lumina, real mode only) ── */
  const refreshPerms = useCallback(async () => {
    if (!isLuminaReal || !config) return;
    // Vendor mode: the legacy `/api/role-permissions` endpoint doesn't
    // exist on the vendor edge (AriaOne ehi.ariaone.ai). Skip the call
    // entirely — the defaults baked into APP_ROLES.lumina already reflect
    // the vendor's role/capability matrix, and per-user scheduler write
    // gates come from `/api/user-access/me` (see permissions.js).
    if (!rolePermissionsApi.isRolePermissionsSupported()) {
      setLoadingPerms(false);
      return;
    }
    setLoadingPerms(true);
    const res = await rolePermissionsApi.listRolePermissions();
    setLoadingPerms(false);
    if (!res.ok) {
      // Silently ignore the vendor-mode soft-fail; toast only for real errors.
      if (res.error?.code !== 'NOT_SUPPORTED_IN_VENDOR_MODE') {
        toast.error('Could not load role permissions', { description: res.error?.message });
      }
      return;
    }
    const byRole = Object.fromEntries((res.data?.roles || []).map((r) => [r.role_id, r.permissions]));
    // Translate backend snake_case keys back to UI labels for rendering
    setPermissions((prev) => {
      const next = { ...prev };
      config.roles.forEach((role) => {
        const backendPerms = byRole[role.id] || {};
        const mapped = {};
        Object.entries(role.permissions).forEach(([label]) => {
          const k = LUMINA_KEY_BY_LABEL[label];
          mapped[label] = k in backendPerms ? !!backendPerms[k] : true;
        });
        next[role.id] = mapped;
      });
      return next;
    });
  }, [isLuminaReal, config]);

  useEffect(() => { refreshPerms(); }, [refreshPerms]);

  // Retry handler for the error state — re-triggers the shared-store fetch.
  const fetchMembers = refreshMembers;

  if (!config) return null;

  const togglePermission = (roleId, perm) => {
    // Optimistic local toggle so the UI feels instant regardless of mode.
    setPermissions((prev) => ({
      ...prev,
      [roleId]: { ...prev[roleId], [perm]: !prev[roleId][perm] },
    }));
    if (!isLuminaReal) return; // demo / non-lumina → local only (per spec)
    // Vendor mode: the persistence endpoint doesn't exist on the vendor
    // edge. Keep the optimistic toggle as a local preference only and
    // skip the network write (a toast would mislead the user into
    // thinking a 404 had occurred).
    if (!rolePermissionsApi.isRolePermissionsSupported()) return;
    const key = LUMINA_KEY_BY_LABEL[perm];
    if (!key) return; // unknown label — backend doesn't persist it
    const nextValue = !permissions[roleId]?.[perm];
    rolePermissionsApi.toggleRolePermission(roleId, key, nextValue).then((res) => {
      if (!res.ok && res.error?.code !== 'NOT_SUPPORTED_IN_VENDOR_MODE') {
        // Revert on real failure
        setPermissions((prev) => ({
          ...prev,
          [roleId]: { ...prev[roleId], [perm]: !nextValue },
        }));
        toast.error('Permission change failed', { description: res.error?.message });
      }
    });
  };

  const savePermissions = async (roleId) => {
    if (!isLuminaReal) {
      toast.success(`${getRoleDef(roleId)?.name} permissions saved (demo)`);
      return;
    }
    if (!rolePermissionsApi.isRolePermissionsSupported()) {
      toast.info(`${getRoleDef(roleId)?.name} permissions are read-only in vendor mode`);
      return;
    }
    const uiPerms = permissions[roleId] || {};
    const payload = {};
    Object.entries(uiPerms).forEach(([label, val]) => {
      const k = LUMINA_KEY_BY_LABEL[label];
      if (k) payload[k] = !!val;
    });
    setSavingRoleId(roleId);
    const res = await rolePermissionsApi.setRolePermissions(roleId, payload);
    setSavingRoleId(null);
    if (res.ok) {
      toast.success(`${getRoleDef(roleId)?.name} permissions saved`);
    } else {
      toast.error('Save failed', { description: res.error?.message });
    }
  };

  const getRoleDef = (roleId) => config.roles.find(r => r.id === roleId);

  return (
    <div className="space-y-4">
      {/* Roles overview */}
      <Card data-testid="roles-overview">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base font-semibold flex items-center gap-2"><Shield className="h-4 w-4 text-slate-500" /> Roles & Permissions</CardTitle>
              <CardDescription className="mt-1">Define what each role can access in {config.name}</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-2">
          {config.roles.map(role => {
            const isExpanded = expandedRole === role.id;
            const perms = permissions[role.id] || role.permissions;
            const enabledCount = Object.values(perms).filter(Boolean).length;
            const totalCount = Object.keys(perms).length;
            return (
              <div key={role.id} className="border rounded-lg overflow-hidden" data-testid={`role-${role.id}`}>
                <button
                  onClick={() => setExpandedRole(isExpanded ? null : role.id)}
                  className="flex items-center gap-3 w-full p-3 text-left hover:bg-slate-50 transition-colors"
                  data-testid={`role-toggle-${role.id}`}
                >
                  {isExpanded ? <ChevronDown className="h-4 w-4 text-slate-400" /> : <ChevronRight className="h-4 w-4 text-slate-400" />}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <Badge className={cn("text-[10px] border-0", role.color)}>{role.name}</Badge>
                      <span className="text-[11px] text-slate-400">{enabledCount}/{totalCount} permissions</span>
                    </div>
                    <p className="text-xs text-slate-500 mt-0.5">{role.desc}</p>
                  </div>
                </button>
                {isExpanded && (
                  <div className="px-4 pb-4 border-t bg-slate-50/50">
                    <div className="grid grid-cols-2 gap-1 mt-3">
                      {Object.entries(perms).map(([perm, enabled]) => (
                        <div key={perm} className="flex items-center justify-between p-2 rounded-lg hover:bg-white transition-colors">
                          <span className="text-xs text-slate-700">{perm}</span>
                          <Switch checked={enabled} onCheckedChange={() => togglePermission(role.id, perm)} className="scale-75" data-testid={`perm-${role.id}-${perm.replace(/\s+/g, '-').toLowerCase()}`} />
                        </div>
                      ))}
                    </div>
                    <Button
                      size="sm"
                      className="mt-3 h-7 text-[11px] bg-black hover:bg-gray-800 gap-1"
                      onClick={() => savePermissions(role.id)}
                      disabled={savingRoleId === role.id}
                      data-testid={`save-perms-${role.id}`}
                    >
                      {savingRoleId === role.id && <Loader2 className="h-3 w-3 animate-spin" />}
                      Save permissions
                    </Button>
                  </div>
                )}
              </div>
            );
          })}
        </CardContent>
      </Card>

      {/* Users — single source of truth: /api/team/members (real) or DEMO_TEAM_MEMBERS (demo) */}
      <Card data-testid="users-list">
        <CardHeader className="flex flex-row items-center justify-between pb-3">
          <div>
            <CardTitle className="text-base font-semibold flex items-center gap-2"><Users className="h-4 w-4 text-slate-500" /> Team Members</CardTitle>
            <CardDescription className="mt-1" data-testid="roles-members-count">
              {usersLoading ? 'Loading members…' : `${users.length} team members (platform-wide)`}
            </CardDescription>
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" className="gap-1 h-8 text-xs" onClick={fetchMembers} data-testid="roles-refresh-members-btn">
              <RefreshCw className="h-3 w-3" /> Refresh
            </Button>
            <Button size="sm" className="bg-black hover:bg-gray-800 gap-1 h-8 text-xs" onClick={() => setShowInvite(true)} data-testid="invite-user-btn">
              <Plus className="h-3 w-3" /> Invite
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {usersLoading && (
            <div className="flex items-center justify-center py-8" data-testid="roles-users-loading">
              <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
              <span className="ml-2 text-xs text-slate-500">Loading members…</span>
            </div>
          )}

          {!usersLoading && usersError && (
            <div className="flex items-center gap-2 p-3 rounded-md bg-red-50 border border-red-100" data-testid="roles-users-error">
              <AlertTriangle className="h-4 w-4 text-red-500" />
              <p className="text-xs text-red-700 flex-1">{usersError.message || 'Could not load members'}</p>
              <Button size="sm" variant="outline" className="text-xs h-7" onClick={fetchMembers}>Retry</Button>
            </div>
          )}

          {!usersLoading && !usersError && users.length === 0 && (
            <div className="py-8 text-center" data-testid="roles-users-empty">
              <Users className="h-8 w-8 text-slate-300 mx-auto mb-2" />
              <p className="text-xs font-medium text-slate-600">No team members yet</p>
              <p className="text-[10px] text-slate-400 mt-1">Invite your first team member to get started.</p>
            </div>
          )}

          {!usersLoading && !usersError && users.length > 0 && (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map(user => {
                const roleDef = getRoleDef(user.role);
                return (
                  <TableRow key={user.id} data-testid={`user-row-${user.id}`}>
                    <TableCell className="font-medium text-sm">{user.name}</TableCell>
                    <TableCell className="text-muted-foreground text-sm">{user.email}</TableCell>
                    <TableCell>
                      <Badge className={cn("text-[10px] border-0", roleDef?.color || 'bg-slate-100 text-slate-600')}>{roleDef?.name || user.role}</Badge>
                    </TableCell>
                    <TableCell>
                      <Badge className={cn("text-[10px] border-0", user.status === 'active' ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500")}>{user.status}</Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => setEditingUser(user)} data-testid={`edit-user-${user.id}`}>
                        <Edit2 className="h-3.5 w-3.5 text-slate-400" />
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          )}
        </CardContent>
      </Card>

      {/* Invite user dialog */}
      <Dialog open={showInvite} onOpenChange={setShowInvite}>
        <DialogContent className="max-w-md" data-testid="invite-user-dialog">
          <DialogHeader>
            <DialogTitle>Invite Team Member</DialogTitle>
            <DialogDescription>Add a new user to {config.name}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><label className="text-xs text-slate-500">First Name</label><Input data-testid="invite-first-name" /></div>
              <div className="space-y-1"><label className="text-xs text-slate-500">Last Name</label><Input data-testid="invite-last-name" /></div>
            </div>
            <div className="space-y-1"><label className="text-xs text-slate-500">Email</label><Input type="email" data-testid="invite-email" /></div>
            <div className="space-y-1">
              <label className="text-xs text-slate-500">Role</label>
              <Select value={inviteRole} onValueChange={setInviteRole}>
                <SelectTrigger data-testid="invite-role-select"><SelectValue placeholder="Select role" /></SelectTrigger>
                <SelectContent>
                  {config.roles.map(r => (<SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowInvite(false)}>Cancel</Button>
            <Button className="bg-black hover:bg-gray-800" onClick={() => { toast.success('Invitation sent'); setShowInvite(false); }} data-testid="send-invite-btn">Send Invite</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit user dialog — wired to shared team store */}
      <Dialog open={!!editingUser} onOpenChange={v => { if (!v) setEditingUser(null); }}>
        <DialogContent className="max-w-md" data-testid="edit-user-dialog">
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
            <DialogDescription>{editingUser?.name}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1">
              <label className="text-xs text-slate-500">Role</label>
              <Select
                value={editingUser?.role || ''}
                onValueChange={(v) => setEditingUser((u) => (u ? { ...u, role: v } : u))}
              >
                <SelectTrigger data-testid="edit-user-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {config.roles.map(r => (<SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <label className="text-xs text-slate-500">Status</label>
              <Select
                value={editingUser?.status || 'active'}
                onValueChange={(v) => setEditingUser((u) => (u ? { ...u, status: v, is_active: v === 'active' } : u))}
              >
                <SelectTrigger data-testid="edit-user-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                  <SelectItem value="suspended">Suspended</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingUser(null)}>Cancel</Button>
            <Button
              className="bg-black hover:bg-gray-800"
              onClick={async () => {
                if (!editingUser) return;
                // Find the original record so we only fire mutations for deltas.
                const original = users.find((u) => u.id === editingUser.id) || {};
                if (original.role !== editingUser.role && editingUser.role) {
                  const r = await storeSetRole(editingUser.id, editingUser.role);
                  if (!r.ok) { toast.error('Role update failed', { description: r.error?.message }); return; }
                }
                if (original.status !== editingUser.status) {
                  const r = await storeUpdateMember(editingUser.id, { status: editingUser.status });
                  if (!r.ok) { toast.error('Status update failed', { description: r.error?.message }); return; }
                }
                toast.success('User updated');
                setEditingUser(null);
              }}
              data-testid="save-user-btn"
            >Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RolesPermissionsTab;
